/**
 * Sorting channels and categories by dragging them in the sidebar (Discord's
 * Modify Guild Channel Positions; the drag itself comes with the redesign).
 * Pure: a drop in, the smallest `PATCH /guilds/{id}/channels` body out. The
 * sidebar's order is `channelTree`'s: the channels outside every category
 * first, then the categories by position; inside one, text-like channels
 * before voice channels (Discord's rule), each by position and then by ID.
 * A position counts inside its own category, so only the dragged item's
 * new neighbours matter: it gets a position between theirs when one is
 * free, and only when none is are its group's positions numbered again.
 */
import {
  ChannelType,
  compareSnowflakes,
  isVoiceChannelType,
  type Channel,
  type ModifyChannelPositionsRequest,
  type Snowflake,
} from '@neosocial/protocol';

/** The server's highest position (`ChannelsService.reorder`). */
export const MAX_CHANNEL_POSITION = 10_000;

export interface ChannelDrop {
  /** The dragged channel or category. */
  id: Snowflake;
  /**
   * The category it lands in; null is the top level (outside every
   * category). A category stays at the top level: a drop of one into a
   * category is refused (an empty body), as the server refuses it.
   */
  parentId: Snowflake | null;
  /**
   * Its place among the items it lands with, as the sidebar shows them
   * without the dragged one: 0 is first. A voice channel never lands among
   * text channels nor the other way round (Discord sorts them apart), so
   * the place is kept inside its own group. For a category, its place
   * among the categories.
   */
  index: number;
  /** Sync its overwrites with the new category (`lock_permissions`), when it changes category. */
  lockPermissions?: boolean;
}

const isCategory = (c: Channel) => c.type === ChannelType.GUILD_CATEGORY;
const isVoice = (c: Channel) => isVoiceChannelType(c.type);
const pos = (c: Channel) => c.position ?? 0;
/** The sidebar's order inside one group: position, then the older channel. */
const cmp = (a: { position: number; id: Snowflake }, b: { position: number; id: Snowflake }) =>
  a.position - b.position || compareSnowflakes(a.id, b.id);
const byPos = (a: Channel, b: Channel) =>
  cmp({ position: pos(a), id: a.id }, { position: pos(b), id: b.id });

/**
 * The body for a drop: the dragged item (with `parent_id` when its category
 * changes) and only the channels whose position must change. Empty when
 * nothing changes or the drop is not possible (an unknown channel or
 * category, a category into a category, a channel into a non-category).
 */
export function channelDropBody(
  channels: readonly Channel[] | Readonly<Record<Snowflake, Channel>>,
  guildId: Snowflake,
  drop: ChannelDrop,
): ModifyChannelPositionsRequest {
  const all = (Array.isArray(channels) ? channels : Object.values(channels)).filter(
    (c: Channel) => c.guild_id === guildId,
  ) as Channel[];
  const dragged = all.find((c) => c.id === drop.id);
  if (!dragged) return [];
  let group: Channel[];
  let parentId: Snowflake | null;
  let index = drop.index;
  if (isCategory(dragged)) {
    if (drop.parentId !== null) return [];
    parentId = null;
    group = all.filter((c) => isCategory(c) && c.id !== dragged.id).sort(byPos);
  } else {
    parentId = drop.parentId;
    if (parentId !== null) {
      const parent = all.find((c) => c.id === parentId);
      if (!parent || !isCategory(parent)) return [];
    }
    const voice = isVoice(dragged);
    const siblings = all
      .filter((c) => !isCategory(c) && c.id !== dragged.id && (c.parent_id ?? null) === parentId)
      .sort((a, b) => Number(isVoice(a)) - Number(isVoice(b)) || byPos(a, b));
    // the place among the siblings as shown, made a place inside its own group
    const before = voice ? siblings.filter((c) => !isVoice(c)).length : 0;
    group = siblings.filter((c) => isVoice(c) === voice);
    index -= before;
  }
  const at = Math.max(0, Math.min(group.length, Math.trunc(index)));
  const order = [...group.slice(0, at), dragged, ...group.slice(at)];
  const moved = (dragged.parent_id ?? null) !== parentId;
  const position = freePosition(order, at, moved ? null : pos(dragged));

  const body: ModifyChannelPositionsRequest = [];
  const entry = (c: Channel, p: number) => {
    const e: ModifyChannelPositionsRequest[number] = { id: c.id };
    if (p !== pos(c)) e.position = p;
    if (c.id === dragged.id && moved) {
      e.parent_id = parentId;
      if (drop.lockPermissions) e.lock_permissions = true;
    }
    if (e.position !== undefined || e.parent_id !== undefined) body.push(e);
  };
  if (position !== null) {
    entry(dragged, position);
    return body;
  }
  // no room between the neighbours: the group numbered 0, 1, 2… in its new order
  order.forEach((c, i) => entry(c, i));
  return body;
}

/**
 * A position for the item at `at` in `order` that sorts it between its
 * neighbours there without moving them: its own (`keep`) when that already
 * does, else the nearest free one. Null when no position does (two
 * neighbours with the same position, or no room at either end).
 */
function freePosition(order: readonly Channel[], at: number, keep: number | null): number | null {
  const self = order[at]!;
  const prev = order[at - 1];
  const next = order[at + 1];
  const fits = (p: number) =>
    p >= 0 &&
    p <= MAX_CHANNEL_POSITION &&
    (!prev || cmp({ position: pos(prev), id: prev.id }, { position: p, id: self.id }) < 0) &&
    (!next || cmp({ position: p, id: self.id }, { position: pos(next), id: next.id }) < 0);
  const candidates: number[] = [];
  if (keep !== null) candidates.push(keep);
  if (prev) candidates.push(pos(prev) + 1, pos(prev));
  if (next) candidates.push(pos(next) - 1, pos(next));
  if (!prev && !next) candidates.push(pos(self), 0);
  return candidates.find(fits) ?? null;
}
